import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize, Subject, takeUntil } from 'rxjs';
import { BoardField, BoardFieldsUpdatedEvent, FieldType } from '../../board-v3.types';
import { BoardService } from '../../../../services/board.service';
import { trimValidator } from '../../../../helpers';
import { NotificationService } from '../../../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import {
  getCustomFieldMaxLength,
  getCustomFieldValidators,
  normalizeCustomFieldValue,
} from '../../custom-field.validators';
import { DialogService } from '../../../../services/dialog.service';
import { CurrencyOption, getCurrencyOptions, normalizePriceAmount } from '../../price-field.utils';
import { BoardV3RealtimeService } from '../../board-v3-realtime.service';

const consecutiveWhitespaceValidator = (control: AbstractControl): Record<string, boolean> | null =>
  /\s{2,}/.test(String(control.value ?? '')) ? { consecutiveWhitespace: true } : null;

interface FieldIconSource {
  icon?: string | null;
  input_type?: string | null;
}

@Component({
  selector: 'app-board-v3-fields-settings',
  templateUrl: './fields-settings.component.html',
  styleUrls: ['./fields-settings.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FieldsSettingsComponent implements OnInit, OnChanges {
  @Input() funnelId: string | null = null;
  @Input() funnelCurrency: string = 'UAH';
  @Input() entityType: string = '';

  @Output() closeChange = new EventEmitter<void>();
  @Output() apply = new EventEmitter<string | void>();

  private readonly boardService = inject(BoardService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly dialogService = inject(DialogService);
  private readonly realtimeService = inject(BoardV3RealtimeService);

  selectedField: any = null;
  createField: any = null;
  conflict: { authorName: string; changes: string[]; event: BoardFieldsUpdatedEvent } | null = null;
  loading = false;
  saving = false;
  fields: BoardField[] = [];
  fieldTypes: FieldType[] = [];
  typeInvalid = false;
  private editorBaselineSnapshot = '';
  private readonly realtimeReset$ = new Subject<void>();
  readonly currencyOptions: CurrencyOption[] = getCurrencyOptions();

  readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, trimValidator, consecutiveWhitespaceValidator, Validators.maxLength(64)],
    }),
    identifier: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(32), Validators.pattern(/^[a-z0-9_]+$/)],
    }),
    default_value: new FormControl<any>(null),
    default_currency: new FormControl<string>('UAH', { nonNullable: true }),
    default_time: new FormControl<string>(''),
    default_today: new FormControl(false, { nonNullable: true }),
    options: new FormControl<string[]>([], { nonNullable: true }),
  });
  readonly optionInput = new FormControl('', { nonNullable: true });

  ngOnInit(): void {
    this.resetAndLoad();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['funnelId']?.currentValue && !changes['funnelId'].firstChange) this.resetAndLoad();
  }

  private resetAndLoad(): void {
    this.realtimeReset$.next();
    this.selectedField = null;
    this.createField = null;
    this.conflict = null;
    this.editorBaselineSnapshot = '';
    if (this.funnelId) this.loadFields();
    if (!this.fieldTypes.length) this.loadFieldTypes();
    this.subscribeToRealtime();
  }

  private subscribeToRealtime(): void {
    if (!this.funnelId) return;

    this.realtimeService
      .fieldsUpdated$(this.funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.realtimeReset$))
      .subscribe((event) => this.applyRealtimeFieldsUpdate(event));
  }

  private loadFields(): void {
    if (!this.funnelId) return;
    this.loading = true;
    this.boardService
      .getBoardFields(this.funnelId, this.entityType)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (fields) => {
          this.loading = false;
          this.fields = fields;
          this.syncSelectedField();
        },
        error: () => {
          this.loading = false;
        },
      });
  }

  private loadFieldTypes(): void {
    this.boardService
      .getFieldTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((types) => (this.fieldTypes = types));
  }

  get allItems(): any[] {
    return this.fields;
  }
  get totalCount(): number {
    return this.fields.length;
  }
  get systemCount(): number {
    return this.fields.filter((v) => v.is_system).length;
  }
  get customCount(): number {
    return this.fields.filter((v) => !v.is_system).length;
  }

  getFieldIcon(item: FieldIconSource | null | undefined): string {
    return item?.icon || this.fieldTypes.find((type) => type.input_type === item?.input_type)?.icon || 'text_fields';
  }

  onFieldDrop(event: CdkDragDrop<any[]>): void {
    if (!this.funnelId || this.saving || event.previousIndex === event.currentIndex) return;
    const draggedField = event.item.data;
    if (draggedField?.is_system) return;

    const firstCustomIndex = this.fields.findIndex((field) => !field.is_system);
    const targetIndex = Math.max(event.currentIndex, firstCustomIndex === -1 ? 0 : firstCustomIndex);
    if (event.previousIndex === targetIndex) return;

    let customIndex = 0;
    const previousFields = [...this.fields];
    moveItemInArray(this.fields, event.previousIndex, targetIndex);
    this.fields = this.fields.map((field) => ({
      ...field,
      position: field.is_system ? field.position : this.getFieldPosition(customIndex++),
    }));
    this.syncSelectedField();

    const payload = this.fields
      .filter((field) => !field.is_system)
      .map((field) => ({
        id: field.id,
        position: field.position,
      }));
    if (!payload.length) return;

    this.saving = true;
    this.boardService
      .reorderFields(this.funnelId, payload, this.entityType)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (fields) => {
          this.fields = Array.isArray(fields) ? fields : this.fields;
          this.syncSelectedField();
          this.markEditorPristine();
          this.notificationService.showSuccess(
            '',
            String(this.translateService.instant('notifications_texts.success.successfully_moved')),
          );
          this.apply.emit();
        },
        error: () => {
          this.fields = previousFields;
          this.syncSelectedField();
          this.notificationService.showError(
            '',
            String(this.translateService.instant('notifications_texts.error.error_update_field_position')),
          );
        },
      });
  }

  selectField(item: any): void {
    this.createField = null;
    this.selectedField = { ...item };
    const defaultDate = this.getDateParts(item.default_value);
    const defaultPrice = this.getPriceDefaultParts(item.default_value);
    this.form.patchValue({
      name: item.name ?? '',
      identifier: item.identifier ?? '',
      default_value:
        item.input_type === 'date'
          ? defaultDate.date
          : item.input_type === 'price'
            ? defaultPrice.amount
            : (item.default_value ?? null),
      default_currency: item.input_type === 'price' ? defaultPrice.currency : this.getInitialPriceCurrency(),
      default_time: defaultDate.time,
      default_today: item.input_type === 'date' && item.default_value === 'today',
      options: this.getFieldOptions(item),
    });
    this.setDefaultValueValidators(item.input_type);
    item.is_system ? this.form.controls.name.disable() : this.form.controls.name.enable();
    this.markEditorPristine();
  }

  selectType(type: FieldType): void {
    const field = this.createField ?? this.selectedField;
    if (!field || field.is_system || this.selectedField) return;
    field.input_type = type.input_type;
    field.data_type = type.data_type;
    if (type.input_type === 'checkbox') field.is_required = false;
    this.form.controls.default_value.reset(null);
    this.form.controls.default_currency.reset(this.getInitialPriceCurrency());
    this.form.controls.default_time.reset('');
    this.form.controls.default_today.reset(false);
    this.form.controls.options.reset([]);
    this.optionInput.reset('');
    this.setDefaultValueValidators(type.input_type);
    this.typeInvalid = false;
  }

  onAddCustomField(): void {
    this.selectedField = null;
    this.createField = { input_type: '', data_type: '', is_required: false, is_readonly: false, is_hidden: false };
    this.form.reset({
      name: '',
      identifier: '',
      default_value: null,
      default_currency: this.getInitialPriceCurrency(),
      default_time: '',
      default_today: false,
      options: [],
    });
    this.optionInput.reset('');
    this.setDefaultValueValidators('');
    this.form.enable();
    this.typeInvalid = false;
    this.markEditorPristine();
  }

  deleteCustomField(item: any): void {
    if (this.saving || item?.is_system || !item?.id) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_delete_field_confirm', { name: item.name }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        if (!result) return;

        this.saving = true;
        this.boardService
          .deleteField(item.id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.saving = false)),
          )
          .subscribe({
            next: () => {
              this.fields = this.fields.filter((field) => field.id !== item.id);
              if (this.selectedField?.id === item.id) this.selectedField = null;
              this.markEditorPristine();
              this.notificationService.showSuccess(
                '',
                this.translateService.instant('notifications_texts.success.successfully_deleted'),
              );
              this.apply.emit(this.fieldKey(item));
            },
            error: () => {
              this.notificationService.showError(
                '',
                this.translateService.instant('notifications_texts.error.error_delete_field'),
              );
            },
          });
      });
  }

  onCancel(): void {
    this.selectedField = null;
    this.createField = null;
    this.markEditorPristine();
  }

  onIdentifierBlur(): void {
    const control = this.form.controls.identifier;
    const value = control.value;
    const duplicate = this.fields.some((field) => !field.is_system && field.identifier === `x_${value}`);
    const errors = { ...(control.errors ?? {}) };
    if (duplicate) errors['duplicate'] = true;
    else delete errors['duplicate'];
    control.setErrors(Object.keys(errors).length ? errors : null);
  }

  onIdentifierInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const normalized = input.value.replace(/\s/g, '_').toLowerCase();
    if (normalized === input.value) return;

    input.value = normalized;
    this.form.controls.identifier.setValue(normalized);
  }

  onDefaultValueBlur(inputType: string): void {
    const control = this.form.controls.default_value;
    const normalized = normalizeCustomFieldValue(inputType, control.value);
    if (normalized !== control.value) control.setValue(normalized);
    control.markAsTouched();
  }

  onPriceInput(event: Event, control: FormControl = this.form.controls.default_value): void {
    const input = event.target as HTMLInputElement;
    const value = input.value.replace(/[^\d.,\s]/g, '');
    if (value === input.value) return;

    input.value = value;
    control.setValue(value);
  }

  getDefaultValueMaxLength(inputType: string): number {
    return getCustomFieldMaxLength(inputType);
  }

  onRequiredChange(required: boolean): void {
    const field = this.createField ?? this.selectedField;
    if (!field) return;
    field.is_required = required;
    if (required) {
      field.is_hidden = false;
      field.is_readonly = false;
    }
  }

  addOption(): void {
    const value = this.optionInput.value.trim();
    const options = this.form.controls.options.value;
    if (
      !value ||
      value.length > 128 ||
      options.some((option) => option.toLocaleLowerCase() === value.toLocaleLowerCase())
    ) {
      return;
    }

    this.form.controls.options.setValue([...options, value]);
    this.optionInput.reset('');
  }

  removeOption(option: string): void {
    const options = this.form.controls.options.value.filter((value) => value !== option);
    this.form.controls.options.setValue(options);
    if (this.form.controls.default_value.value === option) this.form.controls.default_value.setValue(null);
  }

  onOptionInputKeydown(event: KeyboardEvent): void {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    this.addOption();
  }

  getSelectOptions(options: string[] = this.form.controls.options.value): Array<{ label: string; value: string }> {
    return options.map((value) => ({ label: value, value }));
  }

  get isSelectOptionsInvalid(): boolean {
    const field = this.createField ?? this.selectedField;
    return field?.input_type === 'select' && !this.form.controls.options.value.length;
  }

  onCreate(): void {
    const { name, identifier } = this.form.getRawValue();
    const payload = {
      name,
      default_value: this.getDefaultValue(this.createField.input_type),
      options: this.form.controls.options.value,
      data_type: this.createField.data_type,
      input_type: this.createField.input_type,
      identifier: 'x_' + identifier,
      entity_id: this.funnelId,
      entity_type: this.entityType,
      is_required: this.createField.is_required,
      is_readonly: this.createField.is_readonly,
      is_hidden: this.createField.is_hidden,
    };
    this.boardService
      .createField(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (field) => {
          this.saving = false;
          this.createField = null;
          this.fields = [...this.fields, field];
          this.markEditorPristine();
          this.notificationService.showSuccess(
            '',
            String(this.translateService.instant('notifications_texts.success.successfully_created')),
          );
          this.apply.emit();
        },
        error: (error) => {
          this.saving = false;
          this.showApiError(error);
        },
      });
  }

  onUpdate(): void {
    const { name } = this.form.getRawValue();
    const payload = {
      name: name,
      default_value: this.getDefaultValue(this.selectedField.input_type),
      options: this.form.controls.options.value,
      is_required: this.selectedField.is_required,
      is_readonly: this.selectedField.is_readonly,
      is_hidden: this.selectedField.is_hidden,
    };
    this.boardService
      .updateField(this.selectedField.id, payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (field) => {
          this.saving = false;
          const idx = this.fields.findIndex((f) => f.id === this.selectedField.id);
          if (idx !== -1) this.fields[idx] = field;
          this.selectField(field);
          this.notificationService.showSuccess(
            '',
            String(this.translateService.instant('notifications_texts.success.successfully_updated')),
          );
          this.apply.emit();
        },
        error: (error) => {
          this.saving = false;
          this.showApiError(error);
        },
      });
  }

  onSave(): void {
    if (this.saving) return;
    this.form.markAllAsTouched();
    if (this.createField && !this.createField.input_type) this.typeInvalid = true;
    if (this.form.invalid || this.typeInvalid || this.isSelectOptionsInvalid) return;
    this.saving = true;
    if (this.createField) this.onCreate();
    else if (this.selectedField) this.onUpdate();
  }

  private setDefaultValueValidators(inputType: string): void {
    const validators = getCustomFieldValidators(inputType);
    this.form.controls.default_value.setValidators(validators);
    this.form.controls.default_value.updateValueAndValidity({ emitEvent: false });
  }

  private getDefaultValue(inputType: string): unknown {
    const { default_value, default_currency, default_time, default_today } = this.form.getRawValue();
    if (inputType === 'price') {
      return {
        amount: this.normalizePriceDefaultAmount(default_value),
        currency: default_currency || this.getInitialPriceCurrency(),
      };
    }
    if (inputType !== 'date') return default_value || null;
    if (default_today) return 'today';
    if (!default_value) return null;
    return default_time ? this.toUtcIso(default_value, default_time) : default_value;
  }

  private getDateParts(value: unknown): { date: string | null; time: string } {
    if (typeof value !== 'string' || !value || value === 'today') return { date: null, time: '' };
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: value, time: '' };

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: null, time: '' };
    return {
      date: this.toLocalDate(date),
      time: `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`,
    };
  }

  private getPriceDefaultParts(value: unknown): { amount: number | null; currency: string } {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const price = value as Record<string, unknown>;
      return {
        amount: this.normalizePriceDefaultAmount(price['amount']),
        currency: typeof price['currency'] === 'string' ? price['currency'] : this.getInitialPriceCurrency(),
      };
    }

    return {
      amount: this.normalizePriceDefaultAmount(value),
      currency: this.getInitialPriceCurrency(),
    };
  }

  private normalizePriceDefaultAmount(value: unknown): number | null {
    return normalizePriceAmount(value);
  }

  private getInitialPriceCurrency(): string {
    return this.funnelCurrency || 'UAH';
  }

  private toUtcIso(dateValue: string, timeValue: string): string {
    const [year, month, day] = dateValue.split('-').map(Number);
    const [hours, minutes] = timeValue.split(':').map(Number);
    return new Date(year, month - 1, day, hours, minutes).toISOString();
  }

  private toLocalDate(date: Date): string {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  private getFieldOptions(field: any): string[] {
    return Array.isArray(field?.options)
      ? field.options.filter((option: unknown): option is string => typeof option === 'string')
      : [];
  }

  private fieldKey(field: any): string {
    return String(field?.identifier ?? field?.id ?? '');
  }

  private getFieldPosition(index: number): number {
    return index + 1;
  }

  private syncSelectedField(): void {
    if (!this.selectedField?.id) return;

    const selected = this.fields.find((field) => field.id === this.selectedField.id);
    if (selected) this.selectedField = selected;
  }

  private applyRealtimeFieldsUpdate(event: BoardFieldsUpdatedEvent): void {
    if (this.funnelId !== event.funnel_id || this.saving) return;

    const nextFields = event.fields ?? [];
    if (this.hasUnsavedEditorChanges()) {
      this.conflict = {
        authorName: event.author_name || String(this.translateService.instant('board_v3_another_user')),
        changes: this.describeFieldChanges(this.fields, nextFields),
        event,
      };
      return;
    }

    this.applyFieldsSnapshot(nextFields);
  }

  discardMyChanges(): void {
    const event = this.conflict?.event;
    this.conflict = null;
    if (!event) return;

    this.applyFieldsSnapshot(event.fields ?? [], true);
  }

  keepMyChanges(): void {
    this.conflict = null;
  }

  private applyFieldsSnapshot(fields: BoardField[], discardEditor: boolean = false): void {
    this.fields = fields;

    if (this.selectedField?.id) {
      const selected = this.fields.find((field) => field.id === this.selectedField.id);
      if (selected) {
        this.selectField(selected);
      } else {
        this.selectedField = null;
        this.markEditorPristine();
      }
      return;
    }

    if (this.createField && (discardEditor || !this.hasUnsavedEditorChanges())) {
      this.createField = null;
      this.markEditorPristine();
    }
  }

  private hasUnsavedEditorChanges(): boolean {
    if (!this.selectedField && !this.createField) return false;

    return this.editorBaselineSnapshot !== this.getEditorSnapshot();
  }

  private markEditorPristine(): void {
    this.form.markAsPristine();
    this.optionInput.markAsPristine();
    this.editorBaselineSnapshot = this.getEditorSnapshot();
  }

  private getEditorSnapshot(): string {
    const field = this.createField ?? this.selectedField;
    if (!field) return '';

    const formValue = this.form.getRawValue();
    return JSON.stringify({
      id: field.id ?? null,
      mode: this.createField ? 'create' : 'edit',
      input_type: field.input_type ?? '',
      data_type: field.data_type ?? '',
      name: formValue.name ?? '',
      identifier: formValue.identifier ?? '',
      default_value: formValue.default_value ?? null,
      default_currency: formValue.default_currency ?? '',
      default_time: formValue.default_time ?? '',
      default_today: formValue.default_today ?? false,
      options: formValue.options ?? [],
      is_required: field.is_required ?? false,
      is_readonly: field.is_readonly ?? false,
      is_hidden: field.is_hidden ?? false,
    });
  }

  private describeFieldChanges(previousFields: BoardField[], nextFields: BoardField[]): string[] {
    const previousByKey = new Map(previousFields.map((field) => [this.fieldKey(field), field]));
    const nextByKey = new Map(nextFields.map((field) => [this.fieldKey(field), field]));
    const changed = new Set<string>();

    previousByKey.forEach((previous, key) => {
      const next = nextByKey.get(key);
      if (!next) {
        changed.add(this.getFieldName(previous));
        return;
      }

      if (JSON.stringify(previous) !== JSON.stringify(next)) {
        changed.add(this.getFieldName(next));
      }
    });

    nextByKey.forEach((next, key) => {
      if (!previousByKey.has(key)) changed.add(this.getFieldName(next));
    });

    return [...changed];
  }

  private getFieldName(field: BoardField): string {
    if (field.is_system) {
      return String(this.translateService.instant(field.name));
    }

    return field.name;
  }

  private showApiError(error: any): void {
    const code = error?.error?.errors?.[0]?.code;
    const key =
      code === 'CUSTOM_FIELD_ALREADY_EXISTS'
        ? 'errors.custom_field_already_exists'
        : code === 'CUSTOM_FIELD_INVALID'
          ? 'errors.custom_field_invalid'
          : 'errors.default_error';
    this.notificationService.showError('', this.translateService.instant(key));
  }
}
